/**
 * Play-session logging — feeds the external admin activity dashboard.
 *
 * One row per play session in the `sessions` table: when the player logged in
 * (`started_at`) and a `last_seen_at` that a heartbeat keeps fresh while they play,
 * so the dashboard can show login time + session length. Current PROGRESS is NOT
 * stored here — the dashboard reads that live from the player's `saves` row.
 *
 * All calls are best-effort and never throw: monitoring must never disrupt play.
 * RLS lets a player write only their own sessions (see supabase/setup.sql).
 */
import { supabase } from "./supabase";
import type { Account } from "./auth";

let sessionId: number | null = null; // id of the row for the current play session (one per tab)

/** Begin a play session (call once when a logged-in player enters the app). */
export async function startSession(account: Account): Promise<void> {
  if (!supabase) return;
  sessionId = null;
  try {
    const { data, error } = await supabase
      .from("sessions")
      .insert({ user_id: account.id, username: account.username })
      .select("id")
      .single();
    if (!error && data) sessionId = (data as { id: number }).id;
  } catch {
    /* monitoring is non-critical */
  }
}

/** Keep the current session "alive" — bumps last_seen_at so its length stays current. */
export async function heartbeatSession(): Promise<void> {
  if (!supabase || sessionId == null) return;
  try {
    await supabase
      .from("sessions")
      .update({ last_seen_at: new Date().toISOString() })
      .eq("id", sessionId);
  } catch {
    /* non-critical */
  }
}

/** Close the current session (final last_seen_at). Safe to call more than once. */
export async function endSession(): Promise<void> {
  if (!supabase || sessionId == null) return;
  const id = sessionId;
  sessionId = null;
  try {
    await supabase
      .from("sessions")
      .update({ last_seen_at: new Date().toISOString() })
      .eq("id", id);
  } catch {
    /* non-critical */
  }
}
